import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitBranch, FileText, ChevronRight, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

const SEV_DOT = {
  Low: "bg-zinc-300",
  Medium: "bg-amber-400",
  High: "bg-rose-500",
  Critical: "bg-red-600",
};

export default function DocumentTimeline({ docId }) {
  const { t } = useTranslation("components");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);

  const fetchEvolution = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/documents/${docId}/evolution`);
      setData(res.data);
    } catch (e) {
      // silent — most docs have no chain yet
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvolution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  if (loading || !data) return null;
  const nodes = data.nodes || [];
  const isBaseOnly = data.is_base_only;
  const changeLog = data.change_log || [];

  // If there's no chain at all, just show a tiny "Link as modifier" affordance.
  if (isBaseOnly && nodes.length <= 1) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 mb-6"
           data-testid="timeline-empty">
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <GitBranch size={13} className="text-zinc-400" />
          <span>{t("timeline.no_history")}</span>
        </div>
        <LinkAsModifierButton docId={docId} onLinked={fetchEvolution} setShowLink={setShowLink} showLink={showLink} />
      </div>
    );
  }

  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-6" data-testid="document-timeline">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <GitBranch size={14} className="text-zinc-700" strokeWidth={1.75} />
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{t("timeline.section_title")}</p>
          <span className="text-xs text-zinc-500" data-testid="timeline-count">
            {t("timeline.versions", { count: nodes.length })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {nodes.length >= 2 && data.base_doc_id && (
            <Link
              to={`/lineage/${data.base_doc_id}`}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full border border-violet-200 bg-violet-50 text-violet-900 hover:bg-violet-100"
              data-testid="timeline-view-chain"
            >
              <GitBranch size={11} /> {t("timeline.view_full_chain")}
            </Link>
          )}
          <LinkAsModifierButton docId={docId} onLinked={fetchEvolution} setShowLink={setShowLink} showLink={showLink} />
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="timeline-nodes">
        {nodes.map((n, i) => {
          const isCurrent = n.id === docId;
          return (
            <React.Fragment key={n.id}>
              <Link
                to={`/analysis/${n.id}`}
                data-testid={`timeline-node-${n.id}`}
                className={`shrink-0 px-3 py-2 rounded-lg border text-left min-w-[180px] max-w-[240px] transition-colors ${
                  isCurrent
                    ? "border-zinc-950 bg-zinc-950 text-white"
                    : "border-zinc-200 bg-white hover:border-zinc-300"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <FileText size={11} className={isCurrent ? "text-white/60" : "text-zinc-400"} />
                  <p className={`text-[10px] uppercase tracking-wider font-semibold ${isCurrent ? "text-white/60" : "text-zinc-500"}`}>
                    {n.is_base
                      ? t("timeline.node_base")
                      : n.lineage?.relationship_kind
                      ? t(`timeline.${n.lineage.relationship_kind}`, n.lineage.relationship_kind)
                      : t("timeline.node_modifier")}
                    {n.lineage?.sequence ? ` #${n.lineage.sequence}` : ""}
                  </p>
                </div>
                <p className="text-xs font-medium truncate">{n.filename || "—"}</p>
                {n.lineage?.effective_date && (
                  <p className={`text-[10px] mt-0.5 tabular-nums ${isCurrent ? "text-white/50" : "text-zinc-500"}`}>
                    {t("timeline.node_effective", { date: n.lineage.effective_date })}
                  </p>
                )}
              </Link>
              {i < nodes.length - 1 && <ChevronRight size={14} className="text-zinc-400 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {changeLog.length > 0 && (
        <details className="mt-4" data-testid="timeline-changelog">
          <summary className="text-xs font-semibold text-zinc-700 cursor-pointer hover:text-zinc-950">
            {t("timeline.changes_applied", { count: changeLog.reduce((n, c) => n + (c.changes?.length || 0), 0) })}
          </summary>
          <ul className="mt-3 space-y-3">
            {changeLog.map((cl, i) => (
              <li key={i} className="border-l-2 border-zinc-200 pl-3" data-testid={`changelog-${i}`}>
                <p className="text-xs font-semibold text-zinc-900">{cl.modifier_filename}</p>
                <p className="text-[11px] text-zinc-500 mb-2">{cl.summary || t("timeline.no_summary")}</p>
                <ul className="space-y-1.5">
                  {(cl.changes || []).map((ch, j) => (
                    <li key={j} className="flex items-start gap-2 text-[11px]" data-testid={`changelog-${i}-change-${j}`}>
                      <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${SEV_DOT[
                        ch.severity === "Adverse" ? "High" : ch.severity === "Material" ? "Medium" : "Low"
                      ]}`} />
                      <div className="min-w-0">
                        <p className="text-zinc-800 font-medium">{ch.label}</p>
                        <p className="text-zinc-500">
                          <span className="uppercase tracking-wider">{ch.field}</span>
                          {" · "}<span className="uppercase">{ch.severity}</span>
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function LinkAsModifierButton({ docId, onLinked, setShowLink, showLink }) {
  const { t } = useTranslation("components");
  const [candidates, setCandidates] = useState([]);
  const [kind, setKind] = useState("amendment");
  const [parentId, setParentId] = useState("");
  const [effDate, setEffDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!showLink) return;
    api.get(`/documents/${docId}/modifier-candidates`).then((r) => {
      setCandidates(r.data.candidates || []);
    }).catch(() => setCandidates([]));
  }, [showLink, docId]);

  const handleLink = async () => {
    if (!parentId) return;
    setBusy(true);
    try {
      await api.post(`/documents/${docId}/lineage`, {
        parent_doc_id: parentId,
        relationship_kind: kind,
        effective_date: effDate || null,
      });
      toast.success(t("timeline.link_success"));
      setShowLink(false);
      onLinked?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("timeline.link_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowLink((v) => !v)}
        data-testid="timeline-link-button"
        className="h-7 text-xs gap-1.5"
      >
        <GitBranch size={11} />
        {showLink ? t("common:actions.cancel", "Cancel") : t("timeline.link_modifier")}
      </Button>
      {showLink && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
             onClick={() => setShowLink(false)}
             data-testid="timeline-link-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold text-zinc-950 mb-4">{t("timeline.modal_title")}</h3>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">{t("timeline.relationship_kind_label")}</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    data-testid="link-kind-select"
                    className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white mb-3">
              <option value="amendment">{t("timeline.amendment")}</option>
              <option value="endorsement">{t("timeline.endorsement")}</option>
              <option value="modification">{t("timeline.modification")}</option>
              <option value="renewal">{t("timeline.renewal")}</option>
              <option value="restatement">{t("timeline.restatement")}</option>
              <option value="annex">{t("timeline.annex")}</option>
            </select>
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">{t("timeline.effective_date_label")}</label>
            <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)}
                   data-testid="link-effective-date"
                   className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm mb-3" />
            <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">{t("timeline.base")}</label>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)}
                    data-testid="link-parent-select"
                    className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm bg-white mb-4">
              <option value="">{t("timeline.choose_placeholder")}</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>{c.filename}</option>
              ))}
            </select>
            <Button onClick={handleLink} disabled={!parentId || busy}
                    data-testid="link-submit-button"
                    className="w-full h-10 bg-zinc-950 hover:bg-zinc-800 text-white">
              {busy ? t("timeline.linking") : t("timeline.link_button")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
